import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Unique storage key to avoid conflicts with stale sessions
const STORAGE_KEY = 'prosperity-leads-auth';

const isDebug = import.meta.env.DEV || import.meta.env.VITE_DEBUG_AUTH === 'true';
const debugLog = (...args) => { if (isDebug) console.log(...args); };
const debugWarn = (...args) => { if (isDebug) console.warn(...args); };
const debugError = (...args) => { if (isDebug) console.error(...args); };

// Debug logging for environment check (quiet in production)
debugLog('[Supabase Init] Environment check:', {
  hasUrl: !!supabaseUrl,
  hasKey: !!supabaseAnonKey,
  url: supabaseUrl || '(not set)',
});

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    '[Supabase Init] CRITICAL: Missing Supabase environment variables. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.'
  );
}

// Custom storage adapter with logging to debug session issues (quiet in production)
const customStorage = {
  getItem: (key) => {
    try {
      const value = localStorage.getItem(key);
      debugLog('[Supabase Storage] getItem:', key, value ? '(found)' : '(not found)');
      return value;
    } catch (error) {
      debugError('[Supabase Storage] getItem error:', error);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      debugLog('[Supabase Storage] setItem:', key);
      localStorage.setItem(key, value);
    } catch (error) {
      debugError('[Supabase Storage] setItem error:', error);
    }
  },
  removeItem: (key) => {
    try {
      debugLog('[Supabase Storage] removeItem:', key);
      localStorage.removeItem(key);
    } catch (error) {
      debugError('[Supabase Storage] removeItem error:', error);
    }
  },
};

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storageKey: STORAGE_KEY,
    storage: customStorage,
  },
});

/**
 * Anon-only Supabase client for public routes (sharing links, slug redirects).
 * Does NOT persist or refresh auth, so it won't block on CORS/auth failures.
 * Use this for public operations that only need anon RLS (e.g. get_public_event_by_slug, agent_sharing_links).
 */
export const supabaseAnon = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    autoRefreshToken: false,
    persistSession: false,
    detectSessionInUrl: false,
  },
});

// Debug logging to verify client is created (quiet in production)
debugLog('[Supabase Init] Client created:', {
  clientExists: !!supabase,
  hasAuth: !!supabase?.auth,
  authMethods: supabase?.auth ? Object.keys(supabase.auth).slice(0, 5) : [],
});

// Helper to get session with timeout to prevent hanging
// Using 30 second timeout to accommodate browser tab throttling
export const getSessionWithTimeout = async (timeoutMs = 30000) => {
  let timeoutOccurred = false;

  const result = await new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      timeoutOccurred = true;
      debugWarn(`[Supabase] getSession timeout after ${timeoutMs}ms - will retry without clearing storage`);
      resolve({ data: { session: null }, error: new Error('Session fetch timeout'), timedOut: true });
    }, timeoutMs);

    supabase.auth.getSession()
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        debugError('[Supabase] getSession error:', error);
        resolve({ data: { session: null }, error });
      });
  });

  // If timeout occurred, retry once without clearing storage
  // This handles browser tab throttling gracefully
  if (timeoutOccurred) {
    debugLog('[Supabase] Retrying getSession after timeout...');
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (session && !error) {
        debugLog('[Supabase] Retry succeeded, session valid');
        return { data: { session }, error: null };
      }
      debugWarn('[Supabase] Retry completed but no valid session found');
      // Don't clear storage - let the session naturally expire or be refreshed
      return { data: { session: null }, error: error || new Error('Session fetch failed after retry') };
    } catch (retryError) {
      debugError('[Supabase] Retry also failed:', retryError);
      // Still don't clear storage - could be temporary network issue
      return { data: { session: null }, error: retryError };
    }
  }

  return result;
};

// Debug helper to log current storage state
export const debugStorageState = () => {
  if (!isDebug) return;
  try {
    const storageData = localStorage.getItem(STORAGE_KEY);
    if (storageData) {
      const parsed = JSON.parse(storageData);
      debugLog('[Supabase Debug] Storage state:', {
        hasAccessToken: !!parsed?.access_token,
        hasRefreshToken: !!parsed?.refresh_token,
        expiresAt: parsed?.expires_at ? new Date(parsed.expires_at * 1000).toISOString() : null,
        isExpired: parsed?.expires_at ? Date.now() / 1000 > parsed.expires_at : null,
        userId: parsed?.user?.id || null,
      });
    } else {
      debugLog('[Supabase Debug] Storage state: (empty)');
    }
  } catch (error) {
    debugError('[Supabase Debug] Error reading storage:', error);
  }
};

// Helper to clear corrupted session data
export const clearSessionStorage = () => {
  try {
    debugLog('[Supabase] Clearing session storage...');
    localStorage.removeItem(STORAGE_KEY);
    // Also clear the old default key if it exists
    localStorage.removeItem('sb-tsvnduznbicekxzropui-auth-token');
    debugLog('[Supabase] Session storage cleared');
  } catch (error) {
    debugError('[Supabase] Error clearing storage:', error);
  }
};

// Helper to get the current session (legacy, uses direct call)
export const getSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    debugError('Error getting session:', error.message);
    return null;
  }
  return session;
};

// Helper to get the current user
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) {
    debugError('Error getting user:', error.message);
    return null;
  }
  return user;
};

// Log initial storage state on module load (dev only)
debugStorageState();

export default supabase;
